import {PAdbContext} from "../PA.Core/PAdbContext";
import * as _ from 'lodash';
import * as AsyncLock from 'async-lock';
import {WB_PeriodRow, WB_ProductGroupUOMRow, WB_ProductUOMRow} from "../PA.Core/PAdbSchemaRows";
import {esWB_Product_Group_UOM, esWB_Product_UOM} from "../PA.Core/PAdbSchema";
import {PeriodService} from "./PeriodService";
import {DateUtils} from "../PA.Core/Basic/Dates";

export class UOMService {

    private static _dbContext: PAdbContext;

    private static dctWbProductUOMs: {[id: number] : WB_ProductUOMRow[]} = {}; //key is WB_Prod_ID + PRODUCT_UOM
    private static dctWbPrdGrpUOMs: {[id: number] : WB_ProductGroupUOMRow[]} = {}; //key is WB_PrdGrp_ID + PRODUCT_UOM

    private static dctLock_Product = new AsyncLock();
    private static dctLock_PrdGrp = new AsyncLock();

    private static bIsProductLevelLoaded: boolean = false;
    private static bIsPrdGrpLevelLoaded: boolean = false;

    //<editor-fold desc="Product level">

    static async getProductUOMsByProductWbIDUOMId(productWbID: number, uomID: string) : Promise<WB_ProductUOMRow[]>{

        let uomRows = await UOMService.getProductUOMsByProductWbID(productWbID);
        return _.filter(uomRows, (aUOMRow: WB_ProductUOMRow) => {
            return aUOMRow.cif.PRODUCT_UOM == uomID;
        });
    }

    static getProductUOMsByProductWbID(productWbID: number) : Promise<WB_ProductUOMRow[]> {
        return new Promise<WB_ProductUOMRow[]>((resolve, error) => {

            UOMService.dctLock_Product.acquire('productLock',
                (done: any) => {
                    if (productWbID in UOMService.dctWbProductUOMs){
                        done(null, UOMService.dctWbProductUOMs[productWbID]);
                        resolve(UOMService.dctWbProductUOMs[productWbID]);
                        return;
                    } else if (!(productWbID in UOMService.dctWbProductUOMs) && UOMService.bIsProductLevelLoaded) {
                        done(null, UOMService.dctWbProductUOMs[productWbID]);
                        resolve(undefined);
                        return;
                    }

                    UOMService.loadProductUOMs()
                        .then((ignore) => {
                            done(null, UOMService.dctWbProductUOMs[productWbID]);
                            resolve(UOMService.dctWbProductUOMs[productWbID]);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static async getProductUOMsMapByProductWbID(productWbID: number, parentPeriodWbID: number, periodLevelWbID: number) : Promise<{ [p: number]: WB_ProductUOMRow }> {

        let retVal: {[periodWbID: number] : WB_ProductUOMRow} = {};
        let productUOMRows: WB_ProductUOMRow[] = await UOMService.getProductUOMsByProductWbID(productWbID);
        let periodRows: WB_PeriodRow[] = await PeriodService.getAllChildrenByWBIDChildTypeWbId(periodLevelWbID, parentPeriodWbID);
        let aUOMRow: WB_ProductUOMRow;

        for (let aPeriod of periodRows) {
            aUOMRow = UOMService.getFirstProductUOMMatch(productUOMRows,
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT));

            if (!_.isUndefined(aUOMRow)){
                let newUOMRow = UOMService.copyProductUOMRow(aUOMRow);
                newUOMRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                newUOMRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                retVal[aPeriod.cif.WB_PERIOD_ID] = newUOMRow;
            }
        }

        return retVal;
    }

    static async getProductUOMByProductWbIDDateRange(productWbID: number, uomTypeID: string, effectiveStartDt: Date, effectiveEndDt: Date) : Promise<WB_ProductUOMRow> {
        let productUOMs = await UOMService.getProductUOMsByProductWbID(productWbID);
        return _.find(productUOMs, (aUOM) => {
            return DateUtils.RangeDatesOverlap(effectiveStartDt, effectiveEndDt,
                DateUtils.convertFromSAPBW(aUOM.cif.EFFECTIVE_START_DT), DateUtils.convertFromSAPBW(aUOM.cif.EFFECTIVE_END_DT)) &&
                aUOM.cif.PRODUCT_UOM == uomTypeID;
        })
    }

    static async getProductUOMsMapByProductWbIDDateRange(productWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, periodLevel: string) : Promise<{ [p: number]: WB_ProductUOMRow }> {
        let retVal: {[periodWbID: number] : WB_ProductUOMRow} = {};
        let productUOMRows: WB_ProductUOMRow[] = await UOMService.getProductUOMsByProductWbID(productWbID);
        let periodRows: WB_PeriodRow[] = await PeriodService.getPeriodsByPeriodTypeIDDateRange(periodLevel, effectiveStartDt, effectiveEndDt);
        let aUOMRow: WB_ProductUOMRow;

        for (let aPeriod of periodRows){
            aUOMRow = UOMService.getFirstProductUOMMatch(productUOMRows,
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT));

            if (!_.isUndefined(aUOMRow)){
                let newUOMRow = UOMService.copyProductUOMRow(aUOMRow);
                newUOMRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                newUOMRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                retVal[aPeriod.cif.WB_PERIOD_ID] = newUOMRow;
            }
        }
        return retVal;
    }

    private static getFirstProductUOMMatch(uomRows: WB_ProductUOMRow[], startDt: Date, endDt: Date) : WB_ProductUOMRow {
        return _.find(uomRows, (aUOMRow) => {
            return DateUtils.RangeDatesOverlap(startDt, endDt,
                DateUtils.convertFromSAPBW(aUOMRow.cif.EFFECTIVE_START_DT),
                DateUtils.convertFromSAPBW(aUOMRow.cif.EFFECTIVE_END_DT));
        });
    }

    private static copyProductUOMRow(original: WB_ProductUOMRow) : WB_ProductUOMRow {
        let retVal: WB_ProductUOMRow;
        retVal = new WB_ProductUOMRow({
            WB_PROD_ID: original.cif.WB_PROD_ID,
            PRODUCT_UOM: original.cif.PRODUCT_UOM,
            CONVERSION_FACTOR: original.cif.CONVERSION_FACTOR,
            EFFECTIVE_START_DT: original.cif.EFFECTIVE_START_DT,
            EFFECTIVE_END_DT: original.cif.EFFECTIVE_END_DT,
            SOURCE_KEY: original.cif.SOURCE_KEY,
            SOURCE_ID: original.cif.SOURCE_ID
        }, {});
        return retVal;
    }

    private static loadProductUOMs() : Promise<void> {
        return new Promise<void>((resolve, error) => {

            if (_.isUndefined(UOMService._dbContext)){
                UOMService.instantiateDbContext();
            }

            UOMService._dbContext.findAll(esWB_Product_UOM, {})
                .then((results) => {
                    if (_.isArray(results)){
                        //UOMService.dctWbProductUOMs[productWbID] = results as WB_ProductUOMRow[];
                        _.forEach(results, (aResult: WB_ProductUOMRow) => {
                            if (_.isUndefined(UOMService.dctWbProductUOMs[aResult.cif.WB_PROD_ID])) {
                                UOMService.dctWbProductUOMs[aResult.cif.WB_PROD_ID] = [];
                            }
                            UOMService.dctWbProductUOMs[aResult.cif.WB_PROD_ID].push(aResult);
                        });
                        UOMService.bIsProductLevelLoaded = true;
                        resolve();
                    }
                    else {
                        resolve();
                    }
                })
                .catch((err) => {
                    error(err);
                })

        })
    }

    //</editor-fold>

    //<editor-fold desc="Product Group level">

    static async getPrdGrpUOMsByPrdGrpWbIDUOMId(prdGrpWbID: number, uomID: string) : Promise<WB_ProductGroupUOMRow[]>{

        let uomRows = await UOMService.getPrdGrpUOMsByPrdGrpWbID(prdGrpWbID);
        return _.filter(uomRows, (aUOMRow: WB_ProductGroupUOMRow) =>  aUOMRow.cif.PRODUCT_UOM == uomID);
    }

    static getPrdGrpUOMsByPrdGrpWbID(prdGrpWbID: number) : Promise<WB_ProductGroupUOMRow[]> {
        return new Promise<WB_ProductGroupUOMRow[]>((resolve, error) => {

            UOMService.dctLock_PrdGrp.acquire('prdGrpLock',
                (done: any) => {
                    if (prdGrpWbID in UOMService.dctWbPrdGrpUOMs){
                        done(null, UOMService.dctWbPrdGrpUOMs[prdGrpWbID]);
                        resolve(UOMService.dctWbPrdGrpUOMs[prdGrpWbID]);
                        return;
                    } else if (!(prdGrpWbID in UOMService.dctWbPrdGrpUOMs) && UOMService.bIsPrdGrpLevelLoaded) {
                        done(null, UOMService.dctWbPrdGrpUOMs[prdGrpWbID]);
                        resolve(undefined);
                        return;
                    }

                    UOMService.loadPrdGrpUOMs()
                        .then((ignore) => {
                            done(null, UOMService.dctWbPrdGrpUOMs[prdGrpWbID]);
                            resolve(UOMService.dctWbPrdGrpUOMs[prdGrpWbID]);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static async getPrdGrpUOMsMapByPrdGrpWbID(prdGrpWbID: number, parentPeriodWbID: number, periodLevelWbID: number) : Promise<{ [p: number]: WB_ProductGroupUOMRow }> {

        let retVal: {[periodWbID: number] : WB_ProductGroupUOMRow} = {};
        let prdGrpUOMRows: WB_ProductGroupUOMRow[] = await UOMService.getPrdGrpUOMsByPrdGrpWbID(prdGrpWbID);
        let periodRows: WB_PeriodRow[] = await PeriodService.getAllChildrenByWBIDChildTypeWbId(periodLevelWbID, parentPeriodWbID);
        let aUOMRow: WB_ProductGroupUOMRow;

        for (let aPeriod of periodRows) {
            aUOMRow = UOMService.getFirstPrdGrpUOMMatch(prdGrpUOMRows,
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT));

            if (!_.isUndefined(aUOMRow)){
                let newUOMRow = UOMService.copyPrdGrpUOMRow(aUOMRow);
                newUOMRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                newUOMRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                retVal[aPeriod.cif.WB_PERIOD_ID] = newUOMRow;
            }
        }

        return retVal;
    }

    static async getPrdGrpUOMByPrdGrpWbIDDateRange(prdGrpWbID: number, uomTypeID: string, effectiveStartDt: Date, effectiveEndDt: Date) : Promise<WB_ProductGroupUOMRow> {
        let prdGrpUOMs = await UOMService.getPrdGrpUOMsByPrdGrpWbID(prdGrpWbID);
        return _.find(prdGrpUOMs, (aUOM) => {
            return DateUtils.RangeDatesOverlap(effectiveStartDt, effectiveEndDt,
                DateUtils.convertFromSAPBW(aUOM.cif.EFFECTIVE_START_DT), DateUtils.convertFromSAPBW(aUOM.cif.EFFECTIVE_END_DT)) &&
                aUOM.cif.PRODUCT_UOM == uomTypeID;
        })
    }

    static async getPrdGrpUOMsMapByPrdWbIDPeriodWbID(prdGrpWbID: number, uomTypeID: string, parentPeriodWbID: number, periodLevelWbID: number) : Promise<{ [p: number]: WB_ProductGroupUOMRow }> {
        let retVal: {[periodWbID: number] : WB_ProductGroupUOMRow} = {};
        let prdGrpUOMRows: WB_ProductGroupUOMRow[]  = await UOMService.getPrdGrpUOMsByPrdGrpWbID(prdGrpWbID);
        let periodRows: WB_PeriodRow[] = await PeriodService.getAllChildrenByWBIDChildTypeWbId(periodLevelWbID, parentPeriodWbID);
        let aUOMRow: WB_ProductGroupUOMRow;

        _.forEach(periodRows, (aPeriod: WB_PeriodRow) => {
            aUOMRow = UOMService.getFirstPrdGrpUOMMatch(prdGrpUOMRows,
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT));

            if (!_.isUndefined(aUOMRow)){
                let newUOMRow = UOMService.copyPrdGrpUOMRow(aUOMRow);
                newUOMRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                newUOMRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                retVal[aPeriod.cif.WB_PERIOD_ID] = newUOMRow;
            }
        });

        return retVal;
    }

    static async getPrdGrpUOMsMapByPrdGrpWbIDDateRange(prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, periodLevel: string) : Promise<{ [p: number]: WB_ProductGroupUOMRow }> {
        let retVal: {[periodWbID: number] : WB_ProductGroupUOMRow} = {};
        let prdGrpUOMRows: WB_ProductGroupUOMRow[] = await UOMService.getPrdGrpUOMsByPrdGrpWbID(prdGrpWbID);
        let periodRows: WB_PeriodRow[] = await PeriodService.getPeriodsByPeriodTypeIDDateRange(periodLevel, effectiveStartDt, effectiveEndDt);
        let aUOMRow: WB_ProductGroupUOMRow;

        for (let aPeriod of periodRows){
            aUOMRow = UOMService.getFirstPrdGrpUOMMatch(prdGrpUOMRows,
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT),
                DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT));

            if (!_.isUndefined(aUOMRow)){
                let newUOMRow = UOMService.copyPrdGrpUOMRow(aUOMRow);
                newUOMRow.cif.EFFECTIVE_START_DT = aPeriod.cif.PERIOD_START_DT;
                newUOMRow.cif.EFFECTIVE_END_DT = aPeriod.cif.PERIOD_END_DT;
                retVal[aPeriod.cif.WB_PERIOD_ID] = newUOMRow;
            }
        }
        return retVal;
    }

    private static getFirstPrdGrpUOMMatch(uomRows: WB_ProductGroupUOMRow[], startDt: Date, endDt: Date) : WB_ProductGroupUOMRow {
        return _.find(uomRows, (aUOMRow) => {
            return DateUtils.RangeDatesOverlap(startDt, endDt,
                DateUtils.convertFromSAPBW(aUOMRow.cif.EFFECTIVE_START_DT),
                DateUtils.convertFromSAPBW(aUOMRow.cif.EFFECTIVE_END_DT));
        });
    }

    private static copyPrdGrpUOMRow(original: WB_ProductGroupUOMRow) : WB_ProductGroupUOMRow {
        let retVal: WB_ProductGroupUOMRow;
        retVal = new WB_ProductGroupUOMRow({
            WB_PRDGRP_ID: original.cif.WB_PRDGRP_ID,
            PRODUCT_UOM: original.cif.PRODUCT_UOM,
            CONVERSION_FACTOR: original.cif.CONVERSION_FACTOR,
            EFFECTIVE_START_DT: original.cif.EFFECTIVE_START_DT,
            EFFECTIVE_END_DT: original.cif.EFFECTIVE_END_DT,
            SOURCE_KEY: original.cif.SOURCE_KEY,
            SOURCE_ID: original.cif.SOURCE_ID
        }, {});
        return retVal;
    }

    private static loadPrdGrpUOMs() : Promise<void> {
        return new Promise<void>((resolve, error) => {

            if (_.isUndefined(UOMService._dbContext)){
                UOMService.instantiateDbContext();
            }

            UOMService._dbContext.findAll(esWB_Product_Group_UOM, {})
                .then((results) => {
                    if (_.isArray(results)){
                        _.forEach(results, (aResult: WB_ProductGroupUOMRow) => {
                            if (_.isUndefined(UOMService.dctWbPrdGrpUOMs[aResult.cif.WB_PRDGRP_ID])) {
                                UOMService.dctWbPrdGrpUOMs[aResult.cif.WB_PRDGRP_ID] = [];
                            }
                            UOMService.dctWbPrdGrpUOMs[aResult.cif.WB_PRDGRP_ID].push(aResult);
                        });
                        resolve();
                    }
                    else {
                        resolve();
                    }
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    //</editor-fold>

    private static instantiateDbContext() {
        UOMService._dbContext = new PAdbContext({bDisableTracking: true});
    }

}